import React, { ReactElement } from "react";
import {
  Editor,
  EditorState,
  RichUtils,
  getDefaultKeyBinding,
  ContentBlock,
  DraftHandleValue,
  DraftEditorCommand,
} from "draft-js";
import { Row, Col } from "reactstrap";
interface RichEditorProps {
  onChange: (state: EditorState) => void;
  state: EditorState;
  placeholder?: string;
  actionButton?: ReactElement;
}

export class RichEditor extends React.Component<RichEditorProps, {}> {
  private editor: React.RefObject<Editor>;

  constructor(props: any) {
    super(props);
    this.editor = React.createRef();
  }

  private _onChange = (editorState: EditorState) => {
    this.setState({ editorState });
    this.props.onChange(editorState);
  };
  private _focus = () => {
    if (this.editor.current !== null) {
      this.editor.current.focus();
    }
  };

  _handleKeyCommand = (
    command: DraftEditorCommand,
    editorState: EditorState,
    eventTimeStamp: number
  ): DraftHandleValue => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      this._onChange(newState);
      return "handled";
    }
    return "not-handled";
  };

  _mapKeyToEditorCommand = (
    e: React.KeyboardEvent<{}>
  ): DraftEditorCommand | null => {
    if (e.keyCode === 9 /* TAB */) {
      const newEditorState = RichUtils.onTab(
        e,
        this.props.state,
        4 /* maxDepth */
      );
      if (newEditorState !== this.props.state) {
        this._onChange(newEditorState);
      }
      return null;
    }
    return getDefaultKeyBinding(e);
  };

  _toggleBlockType = (blockType: any) => {
    this._onChange(RichUtils.toggleBlockType(this.props.state, blockType));
  };

  _toggleInlineStyle = (inlineStyle: any) => {
    this._onChange(RichUtils.toggleInlineStyle(this.props.state, inlineStyle));
  };

  render() {
    const { state } = this.props;

    // If the user changes block type before entering any text, we can
    // either style the placeholder or hide it. Let's just hide it now.
    let className = "RichEditor-editor";
    var contentState = state.getCurrentContent();
    if (!contentState.hasText()) {
      if (contentState.getBlockMap().first().getType() !== "unstyled") {
        className += " RichEditor-hidePlaceholder";
      }
    }

    return (
      <div className="RichEditor-root">
        <Row>
          <Col
            sm={10}
            md={10}
            lg={10}
            style={{ margin: "auto", display: "inline-flex" }}
          >
            <BlockStyleControls
              editorState={state}
              onToggle={this._toggleBlockType}
            />
            <InlineStyleControls
              editorState={state}
              onToggle={this._toggleInlineStyle}
            />
          </Col>
          <Col sm={2} md={2} lg={2}>
            {this.props.actionButton ? this.props.actionButton : ""}
          </Col>
        </Row>

        <div className={className} onClick={this._focus}>
          <Editor
            blockStyleFn={getBlockStyle}
            customStyleMap={styleMap}
            editorState={state}
            handleKeyCommand={this._handleKeyCommand}
            keyBindingFn={this._mapKeyToEditorCommand}
            onChange={this._onChange}
            placeholder={this.props.placeholder ?? "Write your message"}
            ref={this.editor}
            spellCheck={true}
          />
        </div>
      </div>
    );
  }
}

// Custom overrides for "code" style.
const styleMap = {
  CODE: {
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
    fontSize: 16,
    padding: 2,
  },
};

function getBlockStyle(block: ContentBlock): string {
  switch (block.getType()) {
    case "blockquote":
      return "RichEditor-blockquote";
    default:
      return "";
  }
}

class StyleButton extends React.Component<any, {}> {
  constructor(props: any, private readonly onToggle: (e: any) => void) {
    super(props);
    this.onToggle = (e: MouseEvent) => {
      e.preventDefault();
      this.props.onToggle(this.props.style);
    };
  }

  private renderWithStyle = (name: string) => {
    if (name === "Bold") {
      return <b>{name}</b>;
    } else if (name === "Italic") {
      return <i>{name}</i>;
    } else {
      return <>{name}</>;
    }
  };

  render() {
    let className = "RichEditor-styleButton";
    if (this.props.active) {
      className += " RichEditor-activeButton";
    }

    return (
      <span className={className} onMouseDown={this.onToggle}>
        {this.renderWithStyle(this.props.label)}
      </span>
    );
  }
}

const BLOCK_TYPES = [
  { label: "Large header", style: "header-one" },
  { label: "Small header", style: "header-three" },
  // {label: 'Blockquote', style: 'blockquote'},
  { label: "\u2022", style: "unordered-list-item" },
  { label: "1.", style: "ordered-list-item" },
  // {label: 'Code Block', style: 'code-block'},
];

const BlockStyleControls = (props: any) => {
  const { editorState } = props;
  const selection = editorState.getSelection();
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType();

  return (
    <div className="RichEditor-controls">
      {BLOCK_TYPES.map((type) => (
        <StyleButton
          key={type.label}
          active={type.style === blockType}
          label={type.label}
          onToggle={props.onToggle}
          style={type.style}
        />
      ))}
    </div>
  );
};

var INLINE_STYLES = [
  { label: "Bold", style: "BOLD" },
  { label: "Italic", style: "ITALIC" },
];

const InlineStyleControls = (props: any) => {
  const currentStyle = props.editorState.getCurrentInlineStyle();

  return (
    <div className="RichEditor-controls">
      {INLINE_STYLES.map((type) => (
        <StyleButton
          key={type.label}
          active={currentStyle.has(type.style)}
          label={type.label}
          onToggle={props.onToggle}
          style={type.style}
        />
      ))}
    </div>
  );
};
